/**
 * Create / Edit Bill Modal
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useCreateBill, useUpdateBill } from '../../hooks/useBudgetOverview';
import { categoryService } from '../../services/category-service';
import { CategoryType } from '../../types/category.types';
import type { Bill, BillFrequency, CreateBillInput } from '../../types/bill.types';

interface CreateBillModalProps {
  bill?: Bill | null;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  focusDueDate?: boolean;
}

const FREQUENCY_OPTIONS: { value: BillFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_off', label: 'One-off' },
];

export function CreateBillModal({
  bill,
  onClose,
  onDirtyChange,
  focusDueDate = false,
}: CreateBillModalProps): React.ReactElement {
  const isEdit = !!bill;
  const nameReadOnly = bill?.source === 'scan';
  const closeRef = useRef<HTMLButtonElement>(null);
  const dueDateRef = useRef<HTMLInputElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const createBill = useCreateBill();
  const updateBill = useUpdateBill();

  const [name, setName] = useState(bill?.name ?? '');
  const [displayName, setDisplayName] = useState(bill?.displayName ?? bill?.name ?? '');
  const [categoryId, setCategoryId] = useState(bill?.categoryId ?? '');
  const [amount, setAmount] = useState(bill ? String(bill.amount) : '');
  const [frequency, setFrequency] = useState<BillFrequency>(bill?.frequency ?? 'monthly');
  const [dueDate, setDueDate] = useState(
    bill?.anchorDate ?? bill?.dueDate ?? new Date().toISOString().slice(0, 10)
  );
  const [dayOfMonth, setDayOfMonth] = useState(bill?.dayOfMonth ? String(bill.dayOfMonth) : '');
  const [autopay, setAutopay] = useState(bill?.autopay ?? false);
  const [error, setError] = useState<string | null>(null);

  const isRecurring = frequency !== 'one_off';

  const handleDueDateChange = (value: string) => {
    setDueDate(value);
    if (
      value &&
      (frequency === 'monthly' || frequency === 'quarterly' || frequency === 'yearly') &&
      !dayOfMonth
    ) {
      const day = parseInt(value.slice(8, 10), 10);
      if (day >= 1 && day <= 31) setDayOfMonth(String(day));
    }
  };

  const { data: categoriesData } = useQuery({
    queryKey: ['expense-categories-bills'],
    queryFn: async () => {
      const result = await categoryService.listCategories({ type: CategoryType.EXPENSE });
      if (result.error) throw new Error(result.error.message);
      return result.data?.categories ?? [];
    },
  });

  const categories = categoriesData ?? [];

  useEffect(() => {
    onDirtyChange?.(true);
  }, [name, displayName, categoryId, amount, frequency, dueDate, dayOfMonth, autopay, onDirtyChange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    if (focusDueDate) {
      dueDateRef.current?.focus();
    } else if (nameReadOnly) {
      displayNameRef.current?.focus();
    } else {
      closeRef.current?.focus();
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, focusDueDate, nameReadOnly]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    const trimmedDisplay = displayName.trim() || trimmedName;

    const input: CreateBillInput = {
      name: trimmedName,
      displayName: trimmedDisplay,
      categoryId: categoryId || undefined,
      amount: parseFloat(amount),
      frequency,
      dueDate,
      dayOfMonth: dayOfMonth ? parseInt(dayOfMonth, 10) : undefined,
      autopay,
    };

    try {
      if (isEdit && bill) {
        const { name: _omitName, ...rest } = input;
        await updateBill.mutateAsync({
          id: bill.id,
          input: nameReadOnly ? rest : input,
        });
      } else {
        await createBill.mutateAsync({ ...input, source: 'manual' });
      }
      onDirtyChange?.(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save bill');
    }
  };

  const isPending = createBill.isPending || updateBill.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="create-bill-title">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 id="create-bill-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEdit ? 'Edit Bill' : 'Add Bill'}
            </h2>
            <button ref={closeRef} type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="bill-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bill Name
              </label>
              <input
                id="bill-name"
                type="text"
                value={name}
                onChange={(e) => {
                  const next = e.target.value;
                  setName(next);
                  // Keep display name in sync while user is still typing the primary name
                  // (manual create only — scan bills do not edit name)
                  if (!isEdit && !nameReadOnly && (displayName === '' || displayName === name)) {
                    setDisplayName(next);
                  }
                }}
                readOnly={nameReadOnly}
                required
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 ${
                  nameReadOnly
                    ? 'bg-gray-100 dark:bg-gray-900 cursor-not-allowed text-gray-600 dark:text-gray-400'
                    : 'bg-white dark:bg-gray-700'
                }`}
                aria-readonly={nameReadOnly || undefined}
                title={nameReadOnly ? 'Original name from transaction scan' : undefined}
              />
              {nameReadOnly && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Original label from scan (not editable). Use display name for how this appears on your Budgets page.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="bill-display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display Name
              </label>
              <input
                ref={displayNameRef}
                id="bill-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                placeholder="Friendly name shown on the Budgets page"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Shown on Bills &amp; Reminders and Quick Reminders.
              </p>
            </div>

            <div>
              <label htmlFor="bill-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category (optional)</label>
              <select id="bill-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">No category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="bill-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (AUD)</label>
              <input id="bill-amount" type="number" min={0.01} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="bill-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
                <select id="bill-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value as BillFrequency)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  {FREQUENCY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bill-due-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {isRecurring ? 'Start date' : 'Due date'}
                </label>
                <input
                  ref={dueDateRef}
                  id="bill-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                {isRecurring && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Recurring due dates are calculated from this date. Day of month overrides the day when set.
                  </p>
                )}
              </div>
            </div>

            {(frequency === 'monthly' || frequency === 'quarterly' || frequency === 'yearly') && (
              <div>
                <label htmlFor="bill-day-of-month" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Day of Month (optional)</label>
                <input id="bill-day-of-month" type="number" min={1} max={31} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autopay} onChange={(e) => setAutopay(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Auto-pay enabled</span>
            </label>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="submit" disabled={isPending} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save Changes' : 'Add Bill'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
